
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    productType: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // バリデーション
    if (!formData.name || !formData.email || !formData.message) {
      alert('必須項目を入力してください。');
      return;
    }

    if (formData.message.length > 500) {
      alert('ご相談内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // フォームデータを application/x-www-form-urlencoded 形式で準備
      const formDataToSubmit = new URLSearchParams();
      formDataToSubmit.append('name', formData.name);
      formDataToSubmit.append('company', formData.company);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('phone', formData.phone);
      formDataToSubmit.append('productType', formData.productType || '未選択');
      formDataToSubmit.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d3j02a13290jal8ud5h0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        // フォームをリセット
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          productType: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20business%20consultation%20meeting%20room%20with%20natural%20hot%20spring%20elements%2C%20modern%20office%20interior%20with%20bamboo%20and%20stone%20accents%2C%20warm%20lighting%2C%20clean%20minimalist%20design%2C%20corporate%20wellness%20atmosphere&width=1920&height=600&seq=9&orientation=landscape')`
        }}
      >
        <div className="text-center text-white px-4 w-full">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            お問い合わせ・ご相談
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            温泉成分を活用した製品開発について
            <br />
            お気軽にご相談ください
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                製品開発のご相談
              </h2>
              <p className="text-lg text-gray-600">
                お客様のご要望をお聞かせください。専門スタッフが丁寧にご対応いたします。
              </p>
            </div>

            {/* 送信状況の表示 */}
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-xl mr-2"></i>
                  <span>お問い合わせを送信いたしました。ありがとうございます。</span>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-xl mr-2"></i>
                  <span>送信に失敗しました。しばらく時間をおいて再度お試しください。</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="山田 太郎"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    会社名・団体名
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="株式会社○○○"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="example@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="productType" className="block text-sm font-medium text-gray-700 mb-2">
                  ご希望の製品タイプ
                </label>
                <div className="relative">
                  <select
                    id="productType"
                    name="productType"
                    value={formData.productType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 appearance-none"
                  >
                    <option value="">選択してください</option>
                    <option value="cosmetics">コスメティック製品</option>
                    <option value="bath">入浴剤</option>
                    <option value="both">両方</option>
                    <option value="other">その他</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  ご相談内容・ご質問 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-vertical"
                  placeholder="製品開発についてのご要望、ご質問などをお聞かせください。&#10;&#10;例：&#10;・どのような製品を開発したいか&#10;・予算や数量について&#10;・スケジュールについて&#10;・その他ご不明な点"
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length}/500文字
                </div>
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-8 py-4 rounded-full text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    isSubmitting 
                      ? 'bg-gray-400 text-white cursor-not-allowed' 
                      : 'bg-teal-600 hover:bg-teal-700 text-white'
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      送信中...
                    </span>
                  ) : (
                    'お問い合わせを送信'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              よくあるご質問
            </h2>
            <p className="text-lg text-gray-600">
              お客様からよくいただくご質問をまとめました
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Q. 最小ロット数はどのくらいですか？
              </h3>
              <p className="text-gray-600">
                A. 製品によって異なりますが、コスメティック製品は500個から、入浴剤は1000個からの製造が可能です。詳細はお問い合わせください。
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Q. 開発期間はどのくらいかかりますか？
              </h3>
              <p className="text-gray-600">
                A. 製品の複雑さにもよりますが、企画から製造まで通常3〜6ヶ月程度です。お急ぎの場合はご相談ください。
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Q. パッケージデザインも対応していますか？
              </h3>
              <p className="text-gray-600">
                A. はい、パッケージデザインから製造まで一貫してサポートいたします。お客様のブランドイメージに合わせたデザインをご提案します。
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Q. 見積もりは無料ですか？
              </h3>
              <p className="text-gray-600">
                A. はい、お見積もりは無料です。お気軽にお問い合わせください。詳細な仕様をお聞かせいただければ、より正確なお見積もりをご提供できます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-8">会社情報</h3>
            <div className="space-y-4 text-gray-300">
              <div>
                <span className="font-semibold">社名：</span>
                ONSEN Factory株式会社
              </div>
              <div>
                <span className="font-semibold">代表取締役：</span>
                多田 登
              </div>
              <div>
                <span className="font-semibold">住所：</span>
                宮崎県小林市南西方９６５ー１４
              </div>
              <div>
                <span className="font-semibold">電話番号：</span>
                050-3701-6178
              </div>
              <div>
                <span className="font-semibold">メールアドレス：</span>
                info@onsenfactory.co.jp
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 ONSEN Factory株式会社. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
